import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Overline } from "../components/UI";
import { SEO } from "../components/SEO";
import {
  ShieldCheck,
  ArrowUpRight,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  ABOUT DR TANIA GURDIP SINGH                                        */
/*                                                                    */
/*  Content is verbatim from the approved                              */
/*  "Dr_Tania_About_Page_Content_and_Visual_Pack" (2026-08-03).        */
/*  Visual policy (per pack):                                          */
/*    • Show only the four approved book covers and the NLM catalogue  */
/*      image. No scanned certificates, badges, signatures or          */
/*      correspondence anywhere on this public page.                   */
/*    • Retain every publication and academic contribution in text.    */
/*  DO NOT paraphrase, shorten, reorder, or add promotional claims.    */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  🔒 FROZEN — About Dr Tania Page                                    */
/*  Approved 3 August 2026.                                            */
/*                                                                    */
/*  Content, layout, portrait, section order, book-cover object-fit    */
/*  treatment (object-contain), NLM distinction/image/link,            */
/*  publications DOM-persistence & accessibility, and CTA              */
/*  destinations are FROZEN. See /app/memory/PRD.md.                   */
/*                                                                    */
/*  Do NOT add: sticky consultation button, office hours, certificate  */
/*  gallery, publication scans, admission letters, receipts,           */
/*  signatures, private identifiers, promotional badges, unverified    */
/*  achievements, or claims such as "best" / "world leader" /          */
/*  "most trusted" / guaranteed outcomes.                              */
/*  Do NOT change book covers back to object-fit: cover.               */
/*  Do NOT conditionally unmount the publications panel.               */
/*  Do NOT use "NLM indexed" / "PubMed indexed" / "MEDLINE indexed".   */
/* ------------------------------------------------------------------ */

export function About() {
  return (
    <div
      data-testid="about-page"
      data-frozen="about-page-approved-2026-08-03"
    >
      <SEO
        title="About Dr. Tania Gurdip Singh — Senior High-Risk Pregnancy Consultant"
        description="Clinical judgement for pregnancies that need more than routine care. About Dr Tania Gurdip Singh — obstetrician, gynaecologist, medical author and educator."
      />

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="bg-bone" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="md:col-span-7 order-2 md:order-1">
              <Overline>About</Overline>
              <div className="h-px w-12 bg-prestige my-6" />
              <h1
                data-testid="about-heading"
                className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-navy tracking-tight"
              >
                Clinical judgement for pregnancies that need more than routine care.
              </h1>
              <p
                data-testid="about-subheading"
                className="text-lg md:text-xl text-stone2 mt-6 max-w-2xl leading-relaxed"
              >
                Dr Tania Gurdip Singh is a Senior High-Risk Pregnancy Consultant,
                obstetrician and gynaecologist, medical author and educator. Her
                work centres on recognising risk early, integrating complex
                maternal and fetal information, and helping patients and doctors
                make clear, timely decisions.
              </p>
              <p
                data-testid="about-qual-line"
                className="mono text-xs md:text-[13px] uppercase tracking-[0.18em] text-prestige mt-8 leading-relaxed"
              >
                MBBS &nbsp;·&nbsp; MS (Obstetrics &amp; Gynaecology) &nbsp;·&nbsp;
                FIAOG &nbsp;·&nbsp; Associate Member, Royal College of
                Obstetricians and Gynaecologists, London
              </p>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/brand/dr-tania-mobile-480.webp 480w, /brand/dr-tania-mobile-720.webp 720w"
                  sizes="(min-width: 768px) 38vw, 100vw"
                />
                <img
                  src="/brand/dr-tania-mobile-720.jpg"
                  srcSet="/brand/dr-tania-mobile-480.jpg 480w, /brand/dr-tania-mobile-720.jpg 720w"
                  sizes="(min-width: 768px) 38vw, 100vw"
                  alt="Portrait of Dr. Tania Gurdip Singh"
                  className="w-full aspect-[3/4] object-cover"
                  style={{ objectPosition: "50% 30%" }}
                  loading="eager"
                  width="720"
                  height="960"
                  data-testid="about-portrait"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE CLINICAL MINDSET ─────────────────────────────────── */}
      <Section data-testid="about-clinical-mindset">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <Overline>The Clinical Mindset</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              She looks for the risk before it becomes the crisis.
            </h2>
          </div>
          <div className="md:col-span-8 md:pt-6">
            <p className="text-lg text-stone2 leading-relaxed">
              High-risk pregnancy care is not defined by ordering more tests. It
              is defined by knowing which detail matters, how separate risks
              interact and when waiting is no longer the safest option.
            </p>
            <p className="text-lg text-stone2 leading-relaxed mt-5">
              Dr Tania&rsquo;s consultations bring together the previous pregnancy
              history, current symptoms, laboratory trends, ultrasound findings,
              maternal medical disorders and treatment exposure into one coherent
              clinical picture. The purpose is not merely to name a complication,
              but to anticipate what may happen next and plan while opportunities
              for prevention still remain.
            </p>
            <p className="text-lg text-stone2 leading-relaxed mt-5">
              For the patient, this means detailed medical reasoning explained in
              understandable language. For the referring doctor, it means a
              clearly structured specialist opinion with the important risks,
              priorities and next decisions brought into focus.
            </p>
          </div>
        </div>
      </Section>

      {/* ── PROFESSIONAL STANDING ────────────────────────────────── */}
      <Section className="bg-surfacealt/60" data-testid="about-professional-standing">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <Overline>Professional Standing</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              A specialist identity built across clinical practice, authorship and continuing learning.
            </h2>
          </div>
          <div className="md:col-span-8">
            <dl className="divide-y divide-line border-t border-b border-line">
              {PROFESSIONAL_STANDING.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 py-5"
                  data-testid={`standing-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <dt className="md:col-span-4 mono text-[11px] uppercase tracking-[0.22em] text-prestige">
                    {row.label}
                  </dt>
                  <dd className="md:col-span-8 text-[15.5px] text-navy leading-relaxed">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </Section>

      {/* ── CLINICAL FOCUS ───────────────────────────────────────── */}
      <section className="bg-navy text-white" data-testid="about-clinical-focus">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <Overline className="text-prestige-soft">Clinical Focus</Overline>
              <div className="h-px w-12 bg-prestige my-6" />
              <h2 className="font-serif italic text-3xl md:text-5xl text-white tracking-tight leading-[1.1]">
                High-risk pregnancy is her principal clinical practice.
              </h2>
              <p className="text-white/75 text-lg leading-relaxed mt-6 max-w-xl">
                Her work is centred on pregnancies in which maternal disease,
                previous pregnancy outcomes, fetal concerns or evolving
                complications require closer interpretation, earlier planning
                and coordinated care.
              </p>
            </div>

            <div className="md:col-span-7 md:pt-4">
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4" role="list">
                {CLINICAL_FOCUS_AREAS.map((f) => (
                  <li
                    key={f}
                    data-testid={`focus-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}`}
                    className="flex items-start gap-3 text-white/90 text-[15px] leading-snug"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/60 mt-8 max-w-xl leading-relaxed italic">
                The list is representative rather than exhaustive. Each
                consultation is based on the complete maternal, fetal and
                previous-pregnancy risk profile.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BOOKS & AUTHORSHIP ──────────────────────────────────── */}
      <Section data-testid="about-books">
        <div className="max-w-5xl">
          <Overline>Books &amp; Authorship</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
          <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
            Author of the following books.
          </h2>
          <p className="text-base text-stone2 mt-5 max-w-3xl leading-relaxed">
            Dr Tania Gurdip Singh has authored and co-authored books for
            clinicians, medical students and the wider public. Her work extends
            from case-based obstetrics and bedside learning to obstetric
            emergencies and adolescent health.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {BOOKS.map((book) => (
            <figure
              key={book.title}
              data-testid={`book-${book.slug}`}
              className="flex flex-col"
            >
              <div className="bg-bone border border-line flex items-center justify-center aspect-[2/3] overflow-hidden">
                <img
                  src={book.cover}
                  alt={`Cover of ${book.title} by ${book.authors}`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  width="680"
                  height="1020"
                />
              </div>
              <figcaption className="mt-5">
                <div className="font-serif text-lg text-navy leading-snug">
                  {book.title}
                </div>
                <div className="text-[13px] text-stone2 mt-1.5 leading-relaxed">
                  {book.authors}
                </div>
                <div className="mono text-[11px] uppercase tracking-[0.16em] text-prestige mt-2">
                  {book.publisher}
                </div>
                {book.nlm && (
                  <p className="text-[12.5px] text-stone2 mt-3 leading-relaxed">
                    {book.nlm}
                  </p>
                )}
                {book.ids && (
                  <p className="mono text-[11px] text-stone2/80 mt-2 leading-relaxed break-words">
                    {book.ids}
                  </p>
                )}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* NLM catalogue evidence — restrained documentary panel */}
        <div
          className="mt-14 md:mt-16 border-l-2 border-prestige bg-bone/60 p-6 md:p-8"
          data-testid="about-nlm-panel"
        >
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
            U.S. National Library of Medicine record
          </div>
          <div className="mt-4 grid md:grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="md:col-span-4">
              <img
                src="/about/nlm_clinics_in_obstetrics_catalogue.jpg"
                alt="U.S. National Library of Medicine catalogue record for Clinics in Obstetrics"
                className="w-full border border-line"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-8">
              <p className="font-serif text-lg text-navy leading-snug">
                Clinics in obstetrics ; Tania Gurdip Singh ; foreword, Nutan Jain.
              </p>
              <dl className="mt-4 text-[13.5px] text-stone2 leading-relaxed space-y-1.5">
                <div><span className="text-navy">Singh, Tania G.</span> author.</div>
                <div>New Delhi : Jaypee The Health Sciences Publishers ; 2015</div>
                <div>Library Catalog ; MMS ID 9916728123406676 ; ISBN 9789351521990 (pbk.) ; ISBN 9351521990 (pbk.) ; NLM Unique ID 101672812</div>
                <div>Available at NLM B2 Print (2015 K-681)</div>
              </dl>
              <a
                href="https://www.ncbi.nlm.nih.gov/nlmcatalog/101672812"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View the official NLM catalogue record for Clinics in Obstetrics"
                data-testid="about-nlm-link"
                className="inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-[0.22em] text-prestige hover:text-navy mt-5"
              >
                View official NLM catalogue record
                <ExternalLink size={13} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* ── PUBLICATIONS & ACADEMIC WORK ─────────────────────────── */}
      <Section className="bg-surfacealt/60" data-testid="about-publications">
        <div className="max-w-4xl">
          <Overline>Publications &amp; Academic Work</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
          <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
            A clinical body of work extending beyond the consultation room.
          </h2>
          <p className="text-base text-stone2 mt-5 leading-relaxed">
            Dr Tania has contributed to original research, review articles,
            clinical publications, medical textbooks, professional education and
            public-health writing. The complete documented body of work below is
            presented in text.
          </p>
        </div>

        <PublicationsAccordion />
      </Section>

      {/* ── MEDICINE & RESPONSIBLE TECHNOLOGY ────────────────────── */}
      <Section data-testid="about-ai">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <Overline>Medicine &amp; Responsible Technology</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              Clinical judgement, strengthened by responsible technology.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-[16px] text-stone2 leading-relaxed">
              Alongside her medical and surgical training, Dr Tania has
              undertaken structured education in artificial intelligence and
              machine learning through IIT Delhi, IIIT Hyderabad and the Indian
              Council for Technical Research and Development, Nagpur.
            </p>
            <p className="text-[16px] text-stone2 leading-relaxed mt-5">
              Her interest in this field is practical and clinically grounded:
              improving how complex pregnancy information is organised,
              strengthening evidence retrieval, recognising meaningful risk
              patterns and communicating medical reasoning more clearly.
            </p>
            <p className="text-[16px] text-stone2 leading-relaxed mt-5">
              Technology does not replace clinical judgement. Used responsibly,
              it can help an experienced clinician examine information more
              systematically and prepare more precisely for the decisions that
              matter.
            </p>

            <div className="mt-8">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                Documented programmes
              </div>
              <ul className="mt-4 space-y-3" role="list">
                {AI_PROGRAMMES.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-[14.5px] text-navy leading-relaxed"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* ── ADVANCED PROFESSIONAL TRAINING ───────────────────────── */}
      <Section className="bg-surfacealt/60" data-testid="about-advanced-training">
        <div className="max-w-4xl">
          <Overline>Advanced Professional Training</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
          <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
            Continuous learning across reproductive medicine, surgery and diagnostic care.
          </h2>
        </div>
        <ul
          className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-5"
          role="list"
        >
          {ADVANCED_TRAINING.map((t) => (
            <li
              key={t}
              className="flex items-start gap-3 text-[14.5px] text-navy leading-relaxed border-t border-line pt-5"
            >
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-prestige shrink-0" aria-hidden="true" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-stone2 mt-8 italic max-w-2xl leading-relaxed">
          These programmes are presented as text. Certificates are held privately.
        </p>
      </Section>

      {/* ── PHILOSOPHY OF CARE ───────────────────────────────────── */}
      <Section data-testid="about-philosophy">
        <div className="max-w-3xl">
          <Overline>Philosophy of Care</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
          <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
            Expertise should make the path ahead clearer.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-10">
          {PHILOSOPHY.map((p) => (
            <div
              key={p.num}
              data-testid={`philosophy-${p.num}`}
              className="border-t border-prestige/40 pt-5"
            >
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                {p.num}
              </div>
              <h3 className="font-serif text-2xl text-navy mt-3 leading-tight">
                {p.title}
              </h3>
              <p className="text-[15px] text-stone2 mt-3 leading-relaxed">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── BEYOND CLINICAL PRACTICE ─────────────────────────────── */}
      <Section className="bg-surfacealt/60" data-testid="about-beyond-practice">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <Overline>Beyond Clinical Practice</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              Medical knowledge should reach beyond those who can easily access it.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-[16px] text-stone2 leading-relaxed">
              Through her association with the Hill Mail Foundation, Dr Tania
              has contributed to rural healthcare, free medical consultations,
              antenatal counselling, women&rsquo;s-health awareness, screening
              and public-health education.
            </p>
            <p className="text-[16px] text-stone2 leading-relaxed mt-5">
              Her public-health writing has addressed anaemia, HIV,
              malnutrition, obesity, infant mortality and wider questions
              affecting health in India.
            </p>
          </div>
        </div>
      </Section>

      {/* ── PROFESSIONAL AFFILIATIONS AND RECOGNITION ───────────── */}
      <Section data-testid="about-affiliations">
        <div className="max-w-3xl">
          <Overline>Professional Affiliations and Recognition</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
        </div>
        <ul
          className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-5"
          role="list"
        >
          {AFFILIATIONS.map((a) => (
            <li
              key={a}
              data-testid={`affiliation-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}`}
              className="flex items-start gap-3 border-t border-line pt-4"
            >
              <ShieldCheck
                size={16}
                strokeWidth={1.5}
                className="text-prestige shrink-0 mt-[3px]"
                aria-hidden="true"
              />
              <div className="text-[14.5px] text-navy leading-snug">{a}</div>
            </li>
          ))}
        </ul>
      </Section>

      {/* ── NEXT STEP CTA ────────────────────────────────────────── */}
      <Section className="bg-navy-deep text-white" data-testid="about-next-step">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Overline className="text-prestige-soft">Next Step</Overline>
            <h2 className="font-serif text-3xl md:text-5xl text-white mt-3 tracking-tight leading-tight">
              When the pregnancy is complex, the next step should be clear.
            </h2>
            <p className="text-white/75 mt-5 max-w-2xl text-lg leading-relaxed">
              Patients and referring clinicians may request a consultation for
              preconception planning, recurrent pregnancy loss, a complex
              high-risk pregnancy, review of an evolving complication or a
              carefully reasoned second opinion.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3">
            <Link
              to="/contact?type=appointment"
              data-testid="about-cta-request"
              className="bg-prestige hover:bg-prestige-soft text-navy rounded-full px-6 py-3 text-sm font-medium"
            >
              Request a Consultation
            </Link>
            <Link
              to="/doctors"
              data-testid="about-cta-doctors"
              className="border border-white/40 text-white hover:bg-white hover:text-navy transition-colors rounded-full px-6 py-3 text-sm font-medium"
            >
              For Referring Doctors
            </Link>
            <Link
              to="/conditions"
              data-testid="about-cta-conditions"
              className="text-white/85 hover:text-white text-sm font-medium inline-flex items-center gap-1.5 px-2 py-3"
            >
              Explore High-Risk Conditions{" "}
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Publications accordion — collapsible for readability, retains      */
/*  EVERY item.  Editorial rule: no "selected publications".           */
/* ------------------------------------------------------------------ */

function PublicationsAccordion() {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="publications-toggle"
        className="w-full flex items-center justify-between gap-4 border-t border-b border-navy/20 bg-white py-4 px-5 md:px-6 text-left hover:bg-bone/60 transition-colors"
        aria-expanded={open}
        aria-controls="complete-publications-panel"
      >
        <span className="font-serif text-xl md:text-2xl text-navy leading-tight">
          Complete publications and academic contributions
        </span>
        <span className="mono text-[11px] uppercase tracking-[0.22em] text-prestige inline-flex items-center gap-2">
          {open ? "Hide" : "Show"}
          {open ? (
            <ChevronUp size={16} strokeWidth={1.5} />
          ) : (
            <ChevronDown size={16} strokeWidth={1.5} />
          )}
        </span>
      </button>

      {/* Panel is always mounted so all 15 entries stay in the DOM;
          visibility is toggled via the `hidden` attribute per the approved brief. */}
      <div
        id="complete-publications-panel"
        data-testid="publications-panel"
        hidden={!open}
        className="bg-white border-x border-b border-line px-5 md:px-8 py-8 md:py-10 space-y-10"
      >
          {PUBLICATION_GROUPS.map((group) => (
            <div key={group.heading}>
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                {group.heading}
              </div>
              <ol className="mt-4 space-y-4" start={group.start}>
                {group.items.map((it) => (
                  <li
                    key={it.n}
                    data-testid={`pub-${it.n}`}
                    className="grid grid-cols-[2.25rem_1fr] gap-3 items-start"
                  >
                    <span className="mono text-[12px] text-prestige pt-1">
                      {String(it.n).padStart(2, "0")}
                    </span>
                    <div className="text-[15px] text-navy leading-relaxed">
                      {it.text}
                      {it.note && (
                        <span className="block mt-1 text-[12.5px] text-stone2 italic">
                          {it.note}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
          <p className="text-[12.5px] text-stone2 italic leading-relaxed">
            Every documented item is retained. Public-health articles are
            presented in their original Hindi titles as published in Hill Mail.
          </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Content constants — verbatim from the approved pack.               */
/* ------------------------------------------------------------------ */

const PROFESSIONAL_STANDING = [
  { label: "Clinical Role", value: "Senior High-Risk Pregnancy Consultant" },
  { label: "Medical Qualifications", value: "MBBS · MS (Obstetrics & Gynaecology)" },
  { label: "Professional Fellowship", value: "Fellow, Indian Academy of Obstetrics and Gynaecology (FIAOG)" },
  { label: "International Professional Association", value: "Associate Member, Royal College of Obstetricians and Gynaecologists, London" },
  { label: "Academic Identity", value: "International medical author, researcher and contributor to professional education" },
];

const CLINICAL_FOCUS_AREAS = [
  "Recurrent pregnancy loss and difficult previous obstetric histories",
  "Diabetes and hypertensive disorders in pregnancy",
  "Maternal medical disorders complicating pregnancy",
  "Rh alloimmunisation and fetal anaemia surveillance",
  "Fetal growth restriction, abnormal Dopplers and oligohydramnios",
  "Intrahepatic cholestasis and liver-related pregnancy risks",
  "Autoimmune, renal, neurological and other complex medical conditions",
  "Multiple pregnancy, uterine fibroids and multidisciplinary obstetric care",
];

const BOOKS = [
  {
    slug: "clinics-in-obstetrics",
    title: "Clinics in Obstetrics",
    authors: "Tania Gurdip Singh",
    publisher: "Jaypee The Health Sciences Publishers · 2015",
    cover: "/about/clinics_in_obstetrics_cover.jpg",
    nlm: "Catalogued and held in the print collection of the U.S. National Library of Medicine, Bethesda, Maryland, USA.",
    ids: "NLM Unique ID 101672812 · Call Number 2015 K-681 · ISBN 9789351521990",
  },
  {
    slug: "ward-rounds-obstetrics-neonatology",
    title: "Ward Rounds in Obstetrics & Neonatology",
    authors: "Tania Gurdip Singh and Earl Gaganjot Jaspal",
    publisher: "Jaypee · 2016",
    cover: "/about/ward_rounds_obstetrics_neonatology_cover.jpg",
  },
  {
    slug: "advances-management-obstetric-crisis",
    title: "Advances in the Management of Obstetric Crisis",
    authors: "Tania Singh",
    publisher: "Vol. 1 · 2021",
    cover: "/about/advances_management_obstetric_crisis_cover.jpg",
  },
  {
    slug: "she-is-significant",
    title: "She Is Significant: I Am Entering My Teens",
    authors: "Tania Gurdip Singh and Earl Jaspal",
    publisher: "Blue Rose Publishers · 2023",
    cover: "/about/she_is_significant_cover.jpg",
  },
];

const PUBLICATION_GROUPS = [
  {
    heading: "Original research",
    start: 1,
    items: [
      { n: 1, text: "Comparison of Two WHO Partographs: A One-Year Randomized Controlled Trial. Journal of the Turkish-German Gynecological Association, 2011." },
    ],
  },
  {
    heading: "Review articles and clinical publications",
    start: 2,
    items: [
      { n: 2, text: "Probiotics in Pregnancy. Indian Journal of Nutrition, 2021." },
      { n: 3, text: "Diabetes Mellitus and Planning Conception. Advances in Diabetes & Endocrinology, 2023." },
      { n: 4, text: "Arthritis and Planning Conception. Journal of Orthopedics & Rheumatology, 2023." },
      { n: 5, text: "Blood Transfusion and Blood Components in Obstetrics. AOGD Bulletin, January 2016." },
      { n: 6, text: "Swine Flu in Pregnancy. AOGD Journal, February 2015." },
      { n: 7, text: "Laparoscopic Management of Uterine Fibroids. SELSI Newsletter, May 2013." },
    ],
  },
  {
    heading: "Medical textbook chapters",
    start: 8,
    items: [
      { n: 8, text: "Intraoperative Anesthetic Considerations, in Comprehensive Video Atlas of Laparoscopic Surgery in Infertility and Gynecology, 2016." },
      { n: 9, text: "Endoscopy in Infertility, in Principles and Practice of Assisted Reproductive Technology, 2013." },
      { n: 10, text: "Arterial Suturing, in Step by Step Arterial Surgery.", note: "Full publication details to be verified before final launch." },
    ],
  },
  {
    heading: "Professional education contribution",
    start: 11,
    items: [
      { n: 11, text: "Contributed five peer-reviewed Single Best Answer questions, each with an explanation, for StratOG Core Training tutorials, Royal College of Obstetricians and Gynaecologists, United Kingdom. Letter of appreciation received." },
    ],
  },
  {
    heading: "Public-health writing in Hill Mail",
    start: 12,
    items: [
      { n: 12, text: "एनीमिया — उत्तराखंड में स्वास्थ्य पर बोझ. February 2024." },
      { n: 13, text: "एचआईवी — उत्तराखंड कहाँ खड़ा है? April 2024." },
      { n: 14, text: "आज भारत में स्वास्थ्य कहाँ है? August 2024." },
      { n: 15, text: "कुपोषण, मोटापा और उच्च शिशु मृत्यु दर से कब मिलेगी मुक्ति? January 2025." },
    ],
  },
];

const AI_PROGRAMMES = [
  "Artificial Intelligence and Machine Learning for Industry — Continuing Education Programme, IIT Delhi, 2026.",
  "Orientation Course on AI for Medical Professionals — IIIT Hyderabad, National Academy of Medical Sciences and IIIT-H Data IHub Foundation, 2025.",
  "Artificial Intelligence and Machine Learning Program — Indian Council for Technical Research and Development, 2025.",
];

const ADVANCED_TRAINING = [
  "ICOG–FOGSI six-month Certificate Course in Gynaecological Endoscopy, July–December 2013.",
  "FOGSI Advanced Infertility Training, Sir Ganga Ram Hospital, New Delhi, September 2012.",
  "FOGSI Basic Infertility Training, Dr Nutan Jain's Centre, Muzaffarnagar, March 2013.",
  "Certificate Training in Gynaecological Laparoscopy, Kochi, 2014.",
  "Training in Ultrasonography — Obstetrics, Gynaecology and Abdomen, 2010–2011.",
];

const PHILOSOPHY = [
  {
    num: "01",
    title: "The whole clinical picture",
    body: "No report is interpreted in isolation. Symptoms, trends, medical history, previous pregnancy outcomes and current fetal findings are considered together.",
  },
  {
    num: "02",
    title: "Earlier decisions where prevention remains possible",
    body: "The aim is not only to respond to deterioration, but to identify the stage at which planning may still change the course of the pregnancy.",
  },
  {
    num: "03",
    title: "Medical detail without unnecessary complexity",
    body: "Patients deserve the real reasoning behind a recommendation — explained accurately, calmly and without making them feel intellectually excluded.",
  },
  {
    num: "04",
    title: "A plan that respects the woman",
    body: "The safest medical options are discussed alongside the patient's history, circumstances, concerns and informed choices.",
  },
];

const AFFILIATIONS = [
  "Associate Member, Royal College of Obstetricians and Gynaecologists, London.",
  "Fellow, Indian Academy of Obstetrics and Gynaecology (FIAOG).",
  "Federation of Obstetric and Gynaecological Societies of India (FOGSI).",
  "Indian College of Obstetricians and Gynaecologists (ICOG).",
];

/* ------------------------------------------------------------------ */
/*  DoctorsPortal — unchanged, still exported for /doctors route       */
/* ------------------------------------------------------------------ */

export function DoctorsPortal() {
  const items = [
    { title: "Referral Pathway", desc: "Structured referral for high-risk pregnancy and recurrent loss.", to: "/contact?type=referral", tag: "For Physicians" },
    { title: "Second Opinion", desc: "Considered clinical review with full record.", to: "/contact?type=second_opinion", tag: "For Physicians" },
    { title: "Clinical Protocols", desc: "Publicly readable protocols authored by Dr. Tania.", to: "/articles", tag: "Public Access" },
    { title: "Research Library", desc: "Curated evidence for practising obstetricians.", to: "/research", tag: "Public Access" },
    { title: "Doctor Education", desc: "Deeper technical writing for the medical community.", to: "/articles", tag: "Public Access" },
    { title: "CME Modules", desc: "Structured continuing education. Coming in Phase 2.", to: "#", tag: "Phase 2" },
  ];
  return (
    <div>
      <SEO />
      <div className="bg-navy-deep text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Overline className="text-prestige-soft">For Doctors</Overline>
          <h1 className="font-serif text-5xl md:text-6xl mt-3 tracking-tight leading-[1.05]">A professional lane for the medical community.</h1>
          <p className="text-white/70 mt-6 max-w-2xl text-lg">Referral pathways, clinical protocols, and evidence resources — with doctor-tier clinical insights unlocked after verification.</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/doctors/portal" data-testid="dportal-signin" className="bg-white text-navy hover:bg-white/90 text-sm font-medium px-5 py-2.5 inline-flex items-center gap-2">
              <ShieldCheck size={14} /> Sign in to Doctor Portal
            </Link>
            <Link to="/doctors/register" data-testid="dportal-register" className="border border-white/40 text-white hover:bg-white hover:text-navy text-sm px-5 py-2.5">
              Apply for verification
            </Link>
            <div className="ml-2 inline-flex items-center gap-2 border border-prestige/60 px-4 py-2 rounded-sm mono text-[11px] uppercase tracking-widest text-prestige-soft">
              <ShieldCheck size={14} strokeWidth={1.5} /> Associate Member, Royal College — London
            </div>
          </div>
        </div>
      </div>
      <Section>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((it) => (
            <Link key={it.title} to={it.to} data-testid={`dportal-${it.title.toLowerCase().replace(/\s+/g, "-")}`} className="block bg-white border border-line p-8 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 group">
              <div className="mono text-[10px] uppercase tracking-widest text-prestige">{it.tag}</div>
              <h3 className="font-serif text-2xl text-navy mt-3 leading-tight group-hover:underline underline-offset-4">{it.title}</h3>
              <p className="text-sm text-stone2 mt-3">{it.desc}</p>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

// end of file
